import * as React from 'react';

import { observer } from 'mobx-react';
import styled from 'styled-components';

import { Spinner } from '@estee/elc-spinner';
import { Heading3 } from '@estee/elc-typography';

import { ErrorMessage } from '~components/Message';
import { OTP_FLOW } from '~constants/Constants';
import { classes } from '~utils/Classes';
import { useTranslation } from '~utils/hooks/translations';

import { OtpViewController } from './OtpViewController';
import { OtpViewControllerFactory } from './OtpViewControllerFactory';
import { ResendCode } from './ResendCode';
import { VerificationCode } from './VerificationCode';
import classnames from 'classnames';

const StyledSpinner = styled(Spinner)`
    margin-block: 40px;

    @media (min-width: 1024px) {
        margin-block: 48px;
    }
`;

const LinkButton = styled.a`
    display: inline-block;
    cursor: pointer;
    padding: 0 5px;
`;

const Row = styled.div`
    margin: 15px auto;
`;

const MessageWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const ErrorMessageWrapper = styled.div`
    display: flex;
    flex-flow: column;
    align-items: center;
`;

const ResendCodeWrapper = styled.div`
    padding-left: 4px;
`;

const spinnerClassName = classes('iam-spinner');

export const Otp = observer(() => {
    const [viewController, setViewController] = React.useState<OtpViewController>(
        OtpViewControllerFactory.create()
    );

    const {
        data: {
            allowSwitch,
            needResetToDefault,
            isLoading,
            codeStatus,
            errorMessage,
            numberOfBox,
            enableCountdown,
            countdownSeconds,
            resendCodeBlockThreshold,
            shouldDisplayRetryButton,
            sendToText,
            changeToTranslationKey,
            switchToTranslationKey,
            flow,
            isVerified,
            verifiedCodes
        },
        generateOtpCode,
        resetToDefaultStatus,
        verifyOtpCode,
        onOtpCompleted,
        goBack
    } = viewController;

    React.useEffect(() => {
        const { onInit, loaded } = viewController;
        const autoSendCode = async () => {
            await onInit();
        };

        if (flow === OTP_FLOW.SIGN_UP) {
            autoSendCode();
        }

        loaded();
    }, [viewController, flow]);

    const onSwitchOtpType = async () => {
        const { switchToOtpType } = viewController;
        // Switch the controller
        setViewController(OtpViewControllerFactory.create(switchToOtpType));
    };

    const {
        enterVerificationCode,
        otpInstruction,
        otpNotReceiveCode,
        systemError,
        tryAgain,
        [changeToTranslationKey]: changeTo,
        [errorMessage ?? '']: errorMsg,
        [switchToTranslationKey]: switchTo
    } = useTranslation();

    const wrapperClassName = classnames('elc-iam-verification-wrapper', {
        verified: !!isVerified
    });

    // TODO: #APACFTRS1-1124 handle the placeholder enhancements
    return (
        <StyledSpinner isLoading={isLoading} className={spinnerClassName}>
            <div className={wrapperClassName}>
                <Heading3>{enterVerificationCode}</Heading3>
                <p className="elc-iam-otp-instruction-wrapper">
                    <span
                        dangerouslySetInnerHTML={{
                            __html: otpInstruction?.replace('::OTP_CONTACT::', sendToText)
                        }}
                    />
                    <LinkButton onClick={goBack}>{changeTo}</LinkButton>
                </p>
                <VerificationCode
                    length={numberOfBox}
                    reset={needResetToDefault}
                    status={codeStatus}
                    onCompleted={verifyOtpCode}
                    onChange={resetToDefaultStatus}
                    value={verifiedCodes}
                    readonly={isVerified}
                />
                {errorMessage && (
                    <ErrorMessageWrapper className="elc-iam-otp-error-msg-wrapper">
                        <ErrorMessage message={errorMsg} className="elc-iam-otp-error-msg" />
                    </ErrorMessageWrapper>
                )}
                {shouldDisplayRetryButton && (
                    <MessageWrapper className="elc-iam-otp-info-msg-wrapper">
                        <ErrorMessage message={systemError} className="elc-iam-otp-info-msg" />
                        <LinkButton className="elc-iam-retry-btn" onClick={onOtpCompleted}>
                            {tryAgain}
                        </LinkButton>
                    </MessageWrapper>
                )}
                <MessageWrapper className="elc-iam-otp-resend-code-msg-wrapper">
                    {otpNotReceiveCode}
                    <ResendCodeWrapper className="elc-iam-otp-resend-code-wrapper">
                        <ResendCode
                            enableCountdown={enableCountdown}
                            countdownSeconds={countdownSeconds}
                            resendCodeBlockThreshold={resendCodeBlockThreshold}
                            codeStatus={codeStatus}
                            needReset={needResetToDefault}
                            onClick={generateOtpCode}
                            readonly={isVerified}
                        />
                    </ResendCodeWrapper>
                </MessageWrapper>
                {flow === OTP_FLOW.SIGN_UP && allowSwitch && (
                    <Row>
                        <LinkButton onClick={onSwitchOtpType}>{switchTo}</LinkButton>
                    </Row>
                )}
            </div>
        </StyledSpinner>
    );
});
